import { Box, HStack, Text, VStack, chakra } from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { LuChevronDown } from 'react-icons/lu';
import type { UserProfile } from '@/client';
import { Tooltip } from '@/components/ui/tooltip';
import { AccountMenu } from '../AccountMenu';
import { Avatar } from '@/components/ui/avatar';
import type { Workspace } from './useWorkspaceNav';

interface SidebarUserCardProps {
    user: UserProfile;
    /** Role or stage line under the name. */
    meta?: string;
    /** The home or group currently in view — drives the menu's switcher flyout. */
    workspace?: Workspace | null;
    homes?: Workspace[];
    groups?: Workspace[];
    /** The 76px rail shows the avatar alone. */
    collapsed?: boolean;
}

/**
 * Who you are, pinned to the bottom of the sidebar.
 *
 * This is where the account menu lives now that there is no header row to hang it
 * from. It keeps `aria-label="Account menu"` because that label is how the menu is
 * found — by tests, and by anyone navigating with a screen reader who learned it
 * in the old shell.
 */
export function SidebarUserCard({ user, meta, workspace, homes, groups, collapsed = false }: SidebarUserCardProps) {
    const intl = useIntl();
    const label = intl.formatMessage({
        id: '0680d8aa-abc0-4f8d-8526-93083ff517eb',
        defaultMessage: 'Account menu',
    });

    const avatar = (
        <Avatar size="xs" name={user.name ?? ''} src={user.avatar_url ?? undefined} />
    );

    const trigger = collapsed ? (
        <chakra.button
            type="button"
            mx="auto"
            display="flex"
            bg="transparent"
            borderWidth={0}
            p={0}
            cursor="pointer"
            borderRadius="full"
            aria-label={label}
            _focusVisible={{ outline: '2px solid', outlineColor: 'gold.solid', outlineOffset: '2px' }}
        >
            <Tooltip content={user.name ?? user.email} positioning={{ placement: 'right' }} openDelay={200}>
                {avatar}
            </Tooltip>
        </chakra.button>
    ) : (
        <chakra.button
            type="button"
            w="full"
            display="flex"
            textAlign="left"
            bg="transparent"
            borderWidth={0}
            px={0}
            py="2px"
            cursor="pointer"
            borderRadius="lg"
            aria-label={label}
            _focusVisible={{ outline: '2px solid', outlineColor: 'gold.solid', outlineOffset: '2px' }}
        >
            <HStack gap="11px" w="full" minW={0}>
                {avatar}
                <VStack gap={0} align="flex-start" flex={1} minW={0}>
                    <Text fontSize="13px" fontWeight="semibold" color="fg" truncate maxW="full" lineHeight="1.35">
                        {user.name || user.email}
                    </Text>
                    {meta && (
                        <Text fontSize="11px" fontWeight="medium" color="fg.nav" truncate maxW="full" lineHeight="1.35">
                            {meta}
                        </Text>
                    )}
                </VStack>
                <Box color="fg.nav" flexShrink={0} display="flex">
                    <LuChevronDown size={14} strokeWidth={2} />
                </Box>
            </HStack>
        </chakra.button>
    );

    return (
        <AccountMenu user={user} workspace={workspace} homes={homes} groups={groups}>
            {trigger}
        </AccountMenu>
    );
}
